import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button, Radio, RadioGroup, FormControlLabel, FormControl, Select, MenuItem } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";

interface State {
  stockCheckMngCodes: any[]
}

const defState = {
  stockCheckMngCodes: []
};

interface Info {
  stockCheckMngNo: string,
  mngType: string,
  startLocationId: string,
  locationFrom1: string,
  locationFrom2: string,
  locationFrom3: string,
  locationFrom4: string,
  endLocationId: string,
  locationTo1: string,
  locationTo2: string,
  locationTo3: string,
  locationTo4: string
}

const defInfo = {
  stockCheckMngNo: "",
  mngType: "",
  startLocationId: "",
  locationFrom1: "",
  locationFrom2: "",
  locationFrom3: "",
  locationFrom4: "",
  endLocationId: "",
  locationTo1: "",
  locationTo2: "",
  locationTo3: "",
  locationTo4: ""
};

const validationMsg = [
  {id: "stockCheckMngNo", type: "NotBlank", msg: "실사명을 선택해 주세요."},
  {id: "startLocationId", type: "NotBlank", msg: "시작 실사범위를 선택해 주세요."},
  {id: "startLocationId", type: "Size", msg: "시작 실사범위를 확인해 주세요."},
  {id: "locationFrom1", type: "NotBlank", msg: "시작 실사범위를 선택해 주세요."},
  {id: "locationFrom2", type: "NotBlank", msg: "시작 실사범위를 선택해 주세요."},
  {id: "locationFrom3", type: "NotBlank", msg: "시작 실사범위를 선택해 주세요."},
  {id: "locationFrom4", type: "NotBlank", msg: "시작 실사범위를 선택해 주세요."},
  {id: "endLocationId", type: "NotBlank", msg: "종료 실사범위를 선택해 주세요."},
  {id: "endLocationId", type: "Size", msg: "종료 실사범위를 확인해 주세요."},
  {id: "locationTo1", type: "NotBlank", msg: "종료 실사범위를 선택해 주세요."},
  {id: "locationTo2", type: "NotBlank", msg: "종료 실사범위를 선택해 주세요."},
  {id: "locationTo3", type: "NotBlank", msg: "종료 실사범위를 선택해 주세요."},
  {id: "locationTo4", type: "NotBlank", msg: "종료 실사범위를 선택해 주세요."},
  {id: "skuStockInfos", type: "NotFound", msg: "재고가 없습니다."}
];

function InventoryAuditRegist(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);
  
  const isInit = useRef(false);
  async function init() {
  try {
    if (isInit.current == false) {
      isInit.current = await initData();
    }
    if (isInit.current) {
      await initParams();
      await searchInfo();
    }
  } finally {
    kutil.setLoadingStatus(false);
  }
  }

  async function initData() {
    try {
      const data = await kutil.sendGet("/api/stock/inventory_audit_regist.data?mode=initData", {}, {
        reqFunction: init
      });
      setState(prev => ({ ...prev, stockCheckMngCodes: data.stockCheckMngCodes }));
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  }

  async function initParams() {
  }

  async function searchInfo() {
  }

  async function save() {
    if (info.startLocationId > info.endLocationId) {
      kutil.alert("실사범위가 올바르지 않습니다.");
      return;
    }
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("등록하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/inventory_audit_regist.proc?mode=add", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("등록하였습니다.");
      router.navigateByPath("/stock/inventory_audit_detail", {
        stockCheckNo: data.info.stockCheckNo,
        searchParams: JSON.stringify(JSON.parse(decodeURI(router.query("searchParams") || "{}")))
      });
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/stock/inventory_audit_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  function changeLocation(e: any) {
    const name = e.target.name as string;
    const value = e.target.value as string;
    const isFrom = name.startsWith("locationFrom");
    const sort = Number(name.slice(-1));
    const TOTAL_LEVELS = 4;

    const next: any = { ...info };
    const counter = isFrom ? `locationTo${sort}` : `locationFrom${sort}`;

    next[name] = value;

    if (value === "") {
      next[counter] = "";
      for (let i = sort + 1; i <= TOTAL_LEVELS; i++) {
        next[`locationFrom${i}`] = "";
        next[`locationTo${i}`] = "";
      }
    } else {
      if (next[counter] === "") {
        next[counter] = value;
      }
      for (let i = 1; i < sort; i++) {
        const fromKey = `locationFrom${i}`;
        const toKey = `locationTo${i}`;
        if (next[fromKey] === "") next[fromKey] = "1";
        if (next[toKey] === "") next[toKey] = "1";
      }
    }

    if (sort === 1 && value !== "") {
      const prefix = isFrom ? "locationFrom" : "locationTo";
      const isHigh = Number(value) >= 6;
      if (Number(next[`${prefix}3`]) > (isHigh ? 18 : 45)) next[`${prefix}3`] = "";
      if (Number(next[`${prefix}4`]) > (isHigh ? 15 : 76)) next[`${prefix}4`] = "";
    }

    next.startLocationId = (next.locationFrom1 && next.locationFrom2 && next.locationFrom3 && next.locationFrom4) ? valueFormatter.locationIdParser(next.locationFrom1, next.locationFrom2, next.locationFrom3, next.locationFrom4, "0") : "";
    next.endLocationId = (next.locationTo1 && next.locationTo2 && next.locationTo3 && next.locationTo4) ? valueFormatter.locationIdParser(next.locationTo1, next.locationTo2, next.locationTo3, next.locationTo4, "0") : "";

    setInfo(next);
  }

  return (
    <>
      {/* contents */}
      <div className="contents detail">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>재고실사</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={goList}>취소</Button>
            { auth.write &&
            <Button variant="contained" onClick={save}>등록</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 내용 */}
        <div className="table-wrap emp">
          <table>
            <colgroup>
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "17%" }} />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">실사명<em>*</em></th>
                <td colSpan={7}>
                  <div className="form-base">
                  <FormControl sx={{ m: 1, minWidth: 300 }} size="small">
                    <Select
                      value={info.stockCheckMngNo}
                      onChange={(e) => {setInfo(prev => ({ ...prev, stockCheckMngNo: e.target.value }))}}
                      displayEmpty
                    >
                    <MenuItem value="">선택</MenuItem>
                    {(state.stockCheckMngCodes as any[]).map((code) => (
                      <MenuItem key={code.stockCheckMngNo} value={code.stockCheckMngNo}>{code.mngName}</MenuItem>
                    ))}
                    </Select>
                  </FormControl>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row">구분</th>
                <td colSpan={7}>
                  <div className="form-base">
                    <FormControl>
                      <RadioGroup
                        row
                        value={info.mngType}
                        onChange={(e) => {setInfo(prev => ({ ...prev, mngType: e.target.value }))}}
                      >
                        <FormControlLabel value="" control={<Radio size="small" />} label="전체" />
                        {(commonCode.mngType as any[]).map((code) => (
                          <FormControlLabel key={code.code} value={code.code} control={<Radio size="small" />} label={code.value} />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row">실시범위</th>
                <td colSpan={3}>
                  <div className="ins-select-location">
                  <FormControl>
                    <Select
                      name="locationFrom1"
                      value={info.locationFrom1}
                      onChange={changeLocation}
                      displayEmpty
                    >
                      <MenuItem value="">SC</MenuItem>
                      {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                        const v = String(i + 1);
                        return (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <Select
                      name="locationFrom2"
                      value={info.locationFrom2}
                      onChange={changeLocation}
                      displayEmpty
                    >
                      <MenuItem value="">열</MenuItem>
                      {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                        const v = String(i + 1);
                        return (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <Select
                      name="locationFrom3"
                      value={info.locationFrom3}
                      onChange={changeLocation}
                      displayEmpty
                    >
                      <MenuItem value="">행</MenuItem>
                      {Array.from({ length: (Number(info.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                        const v = String(i + 1);
                        return (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <Select
                      name="locationFrom4"
                      value={info.locationFrom4}
                      onChange={changeLocation}
                      displayEmpty
                    >
                      <MenuItem value="">단</MenuItem>
                      {Array.from({ length: (Number(info.locationFrom1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                        const v = String(i + 1);
                        return (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                    <span>-</span>
                    <FormControl>
                      <Select
                        name="locationTo1"
                        value={info.locationTo1}
                        onChange={changeLocation}
                        displayEmpty
                      >
                        <MenuItem value="">SC</MenuItem>
                        {Array.from({ length: kutil.LOCATION_RANGE.level1 }, (_, i) => {
                          const v = String(i + 1);
                          return (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        name="locationTo2"
                        value={info.locationTo2}
                        onChange={changeLocation}
                        displayEmpty
                      >
                        <MenuItem value="">열</MenuItem>
                        {Array.from({ length: kutil.LOCATION_RANGE.level2 }, (_, i) => {
                          const v = String(i + 1);
                          return (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        name="locationTo3"
                        value={info.locationTo3}
                        onChange={changeLocation}
                        displayEmpty
                      >
                        <MenuItem value="">행</MenuItem>
                        {Array.from({ length: (Number(info.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level3.wp : kutil.LOCATION_RANGE.level3.wb) }, (_, i) => {
                          const v = String(i + 1);
                          return (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <FormControl>
                      <Select
                        name="locationTo4"
                        value={info.locationTo4}
                        onChange={changeLocation}
                        displayEmpty
                      >
                        <MenuItem value="">단</MenuItem>
                        {Array.from({ length: (Number(info.locationTo1) >= kutil.LOCATION_RANGE.startWp ? kutil.LOCATION_RANGE.level4.wp : kutil.LOCATION_RANGE.level4.wb) }, (_, i) => {
                          const v = String(i + 1);
                          return (
                            <MenuItem key={v} value={v}>
                              {v}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </div>
                </td>
                <td colSpan={4}></td>
              </tr>
            </tbody>
          </table>
        </div>
        {/*// 내용 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InventoryAuditRegist);
